/**
 * Client social model — the friends/requests/blocks mirror of the server's
 * seq-acked `socialState` pushes (QuestLog.apply pattern: adopt the whole
 * push, diff the previous state for toasts, bump `rev` so panels re-render).
 *
 * The first push (login seed) fires NO toasts — you shouldn't get "X IS
 * ONLINE" spam for every friend who was already there when you arrived.
 */
import type { SocialFriendWire, SocialMsg, SocialStateMsg } from "@hyperlight/shared";

export class SocialModel {
  /** Bumped on every adopted push — panels compare and re-render. */
  rev = 0;
  friends: SocialFriendWire[] = [];
  incoming: { name: string; at: number }[] = [];
  outgoing: { name: string; at: number }[] = [];
  blocked: { name: string; at: number }[] = [];
  shareSystem = true;
  private seq = 0;
  private seeded = false;

  constructor(private handlers: { toast: (line: string) => void }) {}

  /** Stamp the next op seq (monotonic per session — the quests idiom). */
  op(m: Omit<SocialMsg, "t" | "seq">): SocialMsg {
    return { t: "social", seq: ++this.seq, ...m };
  }

  apply(msg: SocialStateMsg): void {
    const first = !this.seeded;
    if (!first) {
      const hadRequest = new Set(this.incoming.map((e) => e.name));
      const wasFriend = new Set(this.friends.map((f) => f.name));
      const wasOnline = new Set(this.friends.filter((f) => f.online).map((f) => f.name));
      for (const e of msg.incoming) {
        if (!hadRequest.has(e.name)) this.handlers.toast(`FRIEND REQUEST — ${e.name.toUpperCase()}`);
      }
      for (const f of msg.friends) {
        if (!wasFriend.has(f.name)) this.handlers.toast(`NOW FRIENDS WITH ${f.name.toUpperCase()}`);
        else if (f.online && !wasOnline.has(f.name)) this.handlers.toast(`${f.name.toUpperCase()} IS ONLINE`);
      }
    }
    if (msg.err) this.handlers.toast(msg.err);
    this.friends = msg.friends;
    this.incoming = msg.incoming;
    this.outgoing = msg.outgoing;
    this.blocked = msg.blocked;
    this.shareSystem = msg.shareSystem;
    this.seeded = true;
    this.rev++;
  }
}
